import React from "react"
import { VictoryChart, VictoryGroup } from "victory"
import PropTypes from "prop-types"
import XAxis from "./XAxis"
import YAxis from "./YAxis"
import Gradient, { generateGradientFn } from "./Gradient"
import Line from "./Line"
import { Title, PercentUsage, NotEnoughData } from "./labels"
import "./PeakUsageGraph.scss"

const FULL_WIDTH = 700
const FULL_HEIGHT = 200
const BARE_WIDTH = 120
const BARE_HEIGHT = 30

// This is the glue that holds everything together for the peak usage graphs, and all the complex logic
// is delegated to smaller components.
// There's a bit of a weird pattern here where we use components with `YAxis()` instead of `<YAxis />`.
// This is because some of the Victory components that are rendered by our custom components need to be an IMMEDIATE
// child of VictoryChart, so we need to call the component's function directly because using JSX will compile to
// `React.createElement()`, which will add another layer to the component tree.
const PeakUsageGraph = props => {
  const width = props.bare ? BARE_WIDTH : FULL_WIDTH
  const height = props.bare ? BARE_HEIGHT : FULL_HEIGHT

  const assetsUsedPerDay = props.timeslices.map(
    ts => ts && ts.peakUtilizedCount
  )

  const minAssetsUsed = Math.min(...assetsUsedPerDay)
  const maxAssetsUsed = Math.max(...assetsUsedPerDay)
  const howManyPeaks = assetsUsedPerDay.filter(d => d === maxAssetsUsed).length

  const gradientID = "gradient" + Math.random()
  const gradientFn = generateGradientFn(props.maxInEntityCount)
  const possibleAliasing = props.maxInEntityCount > 99 && minAssetsUsed < 1

  const yDomain = [possibleAliasing ? -1 : 0, props.maxInEntityCount]
  const chartProps = {
    standalone: false,
    height,
    width,
    domain: { y: yDomain },
    domainPadding: { x: [2, 2], y: [3, 3] },
    // This allows the y domain padding to expand the domain, ie expand beneath the bottom of the y axis.
    // It also causes aliasing with high-frequency changes
    singleQuadrantDomainPadding: possibleAliasing,
  }

  return (
    <div className="PeakUsageGraph" style={{ width: width, height: height }}>
      <svg viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <Gradient
            gradientFn={gradientFn}
            minAssetsUsed={minAssetsUsed}
            maxAssetsUsed={maxAssetsUsed}
            gradientID={gradientID}
          />
        </defs>

        {props.bare ? (
          <VictoryGroup {...chartProps} padding={0}>
            {Line({
              timeslices: props.timeslices,
              bare: true,
              gradientID,
              gradientFn,
            })}
          </VictoryGroup>
        ) : (
          <VictoryChart {...chartProps}>
            <Title key="title" width={width} />
            <PercentUsage key="percentUsage" />

            {YAxis()}
            {XAxis({
              timeslices: props.timeslices,
              notEnoughData: props.notEnoughData,
              maxAssetsUsed,
              howManyPeaks,
              gradientFn,
              bare: props.bare,
            })}

            {props.notEnoughData ? (
              <NotEnoughData width={width} height={height} />
            ) : (
              Line({
                timeslices: props.timeslices,
                gradientID,
                gradientFn,
                bare: props.bare,
              })
            )}
          </VictoryChart>
        )}
      </svg>
    </div>
  )
}

PeakUsageGraph.propTypes = {
  /* This will become the max of the y axis. */
  maxInEntityCount: PropTypes.number.isRequired,
  /* Each of these will become a point in the graph */
  timeslices: PropTypes.arrayOf(
    PropTypes.shape({
      startsAt: PropTypes.string.isRequired,
      period: PropTypes.oneOf(["day", "week", "month"]).isRequired,
      peakUtilizedCount: PropTypes.number.isRequired,
    }).isRequired
  ).isRequired,
  /* If true, just render a sparkline */
  bare: PropTypes.bool,
  notEnoughData: PropTypes.bool,
}

PeakUsageGraph.defaultProps = {
  maxInEntityCount: 50,
  timeslices: [],
  bare: false,
  notEnoughData: false,
}

export default PeakUsageGraph
